import { FC, useEffect, useRef, useState } from "react";
import {
  apiGetShred,
  apiGetShredProgress,
  apiRegenerateShred,
  apiResumeShredGeneration,
  apiStopShred,
  pushShredToRepo
} from "@/services/ShredService";
import { ShredResponse } from "@/@types/shred.ts";
import Dependencies from "@/components/architecture/Dependencies.tsx";
import { EditorState } from "draft-js";
import Apis from "@/components/architecture/Apis.tsx";
import GithubRepoBrowser from "@/components/GithubRepoBrowser";
import Button from "@/components/ui/Button/index";
import Tabs from "@/components/ui/Tabs/index";
import PublishForReview from "@/components/architecture/PublishForReview";
import ShredDetails from "@/components/architecture/ShredDetails";
import { Github, GitPullRequest, RefreshCw, Upload } from "lucide-react";
import CKEditorReadonly from "@/components/CKEditorReadonly.tsx";
import GenerationStepper from "@/components/architecture/GenerationStepper";

const { TabNav, TabList, TabContent } = Tabs;

interface StepFiveProps {
  onStepError: (error: string) => void;
  shredId: string | undefined;
  shredDescription: string;
  shredPlan: string;
  shredName: string;
  shredResponse: any;
  shredStartDate: string;
  shredProgress: number;
  shredCoinsUsed: string;
  shredGenerationTime: string;
}

interface Api {
  input: {
    body: object | string;
    description: string;
  };
  isApi: boolean;
  output: Output[];
}

interface Output {
  status: string;
  body: object | string;
  description: string;
}

interface externalDependencies {
  description: EditorState;
  type: string;
}

const StepFive: FC<StepFiveProps> = (props) => {
  const {
    onStepError,
    shredId,
    shredResponse,
    shredName,
    shredDescription,
    shredPlan,
    shredStartDate,
    shredProgress,
    shredCoinsUsed,
    shredGenerationTime
  } = props;
  
  const [apis, setApis] = useState<Api[]>([]);
  const [dependency, setDependency] = useState<externalDependencies[]>([]);
  const [githubLink, setGithubLink] = useState<string>("");
  
  const [inProgress, setInProgress] = useState<boolean>(true);
  const [repoUploaded, setRepoUploaded] = useState<boolean>(false);
  const [shredDone, setShredDone] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [githubKey, setGithubKey] = useState<string>("");
  const [isShredModel, setIsShredModel] = useState<boolean>(false);
  const [_pushToGithubFailed, setPushToGithubFailed] = useState<boolean>(false);
  
  const [startDate, setStartDate] = useState<string>(shredStartDate);
  const [progress, setProgress] = useState<number>(shredProgress);
  const [coinsUsed, setCoinsUsed] = useState<string>(shredCoinsUsed);
  const [generationTime, setGenerationTime] = useState<string>(shredGenerationTime);
  
  const [regenerateShredLoading, setRegenerateShredLoading] = useState<boolean>(false);
  
  // State variables for the stepper
  const [currentStep, setCurrentStep] = useState<string>("GENERATING_UML");
  const [isPRScenario, setIsPRScenario] = useState<boolean>(false);
  const [failedStep, setFailedStep] = useState<string>(""); // New state for failed step
  const [generationState, setGenerationState] = useState<"ACTIVE" | "DONE" | "FAILED" | "STOPPING" | "STOPPED">("ACTIVE");
  
  // Add ref for IDE section and scroll control
  const ideRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToIDE, setHasScrolledToIDE] = useState<boolean>(false);
  
  // New state variables for push functionality
  const [failedToPush, setFailedToPush] = useState<boolean>(false);
  const [pushLoading, setPushLoading] = useState<boolean>(false);
  
  const loadShred = async () => {
    try {
      const response = await apiGetShred(shredId);
      
      const ap: Api[] = [];
      if (response && "data" in response) {
        console.log("DEBUG - StepFive.tsx - response.data:", response.data);
        console.log("DEBUG - StepFive.tsx - repoLink value:", response.data.repoLink);
        console.log("DEBUG - StepFive.tsx - repoLink type:", typeof response.data.repoLink);
        
        const isInvalidRepoLink = !response.data.repoLink || 
                                 response.data.repoLink.trim() === "" || 
                                 response.data.repoLink.trim() === "https://github.com/" ||
                                 response.data.repoLink.trim() === "https://github.com";
        
        console.log("DEBUG - StepFive.tsx - is repoLink invalid?", isInvalidRepoLink);
        
        if (isInvalidRepoLink) {
          console.log("DEBUG - StepFive.tsx - TRIGGERING WARNING OVERLAY - repoLink is invalid");
          setGithubLink("");
        } else {
          console.log("DEBUG - StepFive.tsx - repoLink is valid, setting githubLink");
          setGithubLink(
            response.data.repoLink.replace("github.com", "github1s.com")
          );
        }
        
        response.data.prompt.entry.map((app: any) => {
          const op = app.outputs.map((out: any) => {
            return {
              status: out.httpStatus,
              body: JSON.parse(out.example),
              description: out.description
            };
          });
          ap.push({
            input: {
              body: JSON.parse(app.inputs[0].example),
              description: app.inputs[0].description
            },
            isApi: app.inputs[0].api,
            output: op
          });
        });
        
        setApis(ap);
        setDependency(response.data.prompt.external);
      } else {
        onStepError("An error occurred while fetching the shred");
      }
    } catch (error: any) {
      onStepError(error?.response?.data?.message || error?.message);
    }
  };
  
  const getStatus = () => {
    onStepError("");
    
    apiGetShredProgress(shredId)
      .then(value => {
        const apiData = value.data.body;
        
        // Set the Shred Card information
        setStartDate(apiData.startDate);
        setProgress(apiData.progress);
        setCoinsUsed(apiData.coinsUsed);
        setGenerationTime(apiData.generationTime);
        setIsShredModel(apiData.model);
        setPushToGithubFailed(apiData.failed);
        
        // Check for Failed To Push condition
        setFailedToPush(apiData.failedToPush || false);
        
        // Handle current step for stepper - use 'step' field from API
        if (apiData.step) {
          setCurrentStep(apiData.step);
          
          // Determine if it's a PR scenario
          if (apiData.step === "ADDRESSING_PR_REVIEW") {
            setIsPRScenario(true);
          }
        }
        
        // Set repo uploaded status
        setRepoUploaded(apiData.repoUploaded || false);
        
        // Set countdown from API - ensure it's a number
        const estimateTime = parseInt(apiData.estimateTime) || 0;
        setCountdown(estimateTime);
        
        // Set the shred generation state
        setGenerationState(apiData.generationState);
        setFailedStep("");
        
        // Handle different statuses
        if (apiData.step === "DONE" || (apiData.status === "DONE" && apiData.repoUploaded && !apiData.step)) {
          // Truly completed - both status and step are done OR old API format
          setHasError(false);
          setInProgress(false);
          setShredDone(true);
          setCurrentStep("DONE");
        } else if (apiData.status === "FAILED") {
          // Failed - set the failed step to current step
          setHasError(true);
          setFailedStep(apiData.step || currentStep);
          setInProgress(false);
          setShredDone(false);
          
          // Continue polling for a possible resume
          setTimeout(() => getStatus(), 5000);
        } else if (apiData.status === "STOPPED") {
          // Stopped - set the failed step to current step
          setHasError(false);
          setInProgress(false);
          setShredDone(false);
        } else {
          // Still processing - show stepper
          setHasError(false);
          setInProgress(true);
          setShredDone(false);
          
          // Continue polling
          setTimeout(() => getStatus(), 15000);
        }
      })
      .catch((_error) => {
        onStepError("An error occurred while fetching the shred progress. Refresh the page to try again.");
      });
  };
  
  const onShredResume = async () => {
    if (shredId === undefined) return;
    
    try {
      setLoading(true);
      
      const response = await apiResumeShredGeneration(shredId);
      if (response.data) {
        handleShredResponse(response.data);
      }
    } catch (errors: any) {
      const errorMessage = errors?.response?.data?.body?.message || 
                          errors?.response?.data?.message || 
                          errors?.message || 
                          "An error occurred while resuming the shred generation";
      onStepError(errorMessage);
    }
    
    setLoading(false);
  };
  
  const onShredStop = async () => {
    if (!shredId) return;
    setLoading(true);
    try {
      await apiStopShred(shredId);
      window.location.reload();
    } catch (err) {
      onStepError("An error occurred while stopping the shred.");
    } finally {
      setLoading(false);
    }
  };
  
  const onRegenerateShred = () => {
    if (!shredId) return;
    
    setRegenerateShredLoading(true);
    
    // Reset all states for regeneration
    setCurrentStep("GENERATING_UML");
    setIsPRScenario(false);
    setInProgress(true);
    setShredDone(false);
    setHasError(false);
    setFailedStep(""); // Reset failed step
    setHasScrolledToIDE(false); // Reset scroll state
    setFailedToPush(false); // Reset push failure state
    
    apiRegenerateShred(shredId)
      .then((response) => {
        if (response.data) {
          handleShredResponse(response.data);
        }
        
        setGithubKey(Date.now().toString());
      })
      .catch((err) => {
        if ("response" in err && "data" in err.response) {
          if ("body" in err.response.data) {
            onStepError(err.response.data.body.message);
          } else {
            onStepError(err.response.data.message);
          }
        } else {
          onStepError("An error occurred while generating the shred");
        }
      })
      .finally(() => {
        setRegenerateShredLoading(false);
      });
  };
  
  // New function to handle push to repository
  const handlePushToRepo = async () => {
    if (!shredId) return;
    
    setPushLoading(true);
    try {
      await pushShredToRepo(shredId);
      
      // Reset failed push state and refresh GitHub browser
      setFailedToPush(false);
      setGithubKey(Date.now().toString());
      
      // Refresh the status to get updated repo state
      getStatus();
    } catch (error: any) {
      onStepError(error?.response?.data?.message || error?.message || "Failed to push to repository");
    } finally {
      setPushLoading(false);
    }
  };
  
  const handleShredResponse = (shredResponse: ShredResponse) => {
    if (shredResponse) {
      if (shredResponse.status === 200) {
        // Handle step updates from response
        if (shredResponse.body.step) {
          setCurrentStep(shredResponse.body.step);
        }
        
        // Set countdown from response - ensure it's a number
        const estimateTime = parseInt(shredResponse.body.estimateTime) || 0;
        setCountdown(estimateTime);
        
        // Set the shred generation state
        setGenerationState(shredResponse.body.generationState);
        setFailedStep("");
        
        if (shredResponse.body.step === "DONE" || (shredResponse.body.status === "DONE" && shredResponse.body.repoUploaded)) {
          setHasError(false);
          setInProgress(false);
          setShredDone(true);
          setCurrentStep("DONE");
        } else if (shredResponse.body.status === "FAILED") {
          setHasError(true);
          setFailedStep(shredResponse.body.step || currentStep);
          setInProgress(false);
          setShredDone(false);
        } else if (shredResponse.body.status === "STOPPED") {
          // Handle STOPPED status
          setHasError(false);
          setInProgress(false);
          setShredDone(false);
        } else {
          setHasError(false);
          setInProgress(true);
          setShredDone(false);
          
          setTimeout(() => getStatus(), 10000);
        }
      }
    }
  };
  
  const handlePublishForReview = () => {
    setGithubKey(Date.now().toString());
    
    // Set PR scenario when publishing for review
    setIsPRScenario(true);
    setCurrentStep("ADDRESSING_PR_REVIEW");
    
    getStatus();
  };
  
  const handlePublishError = (error: string) => {
    onStepError(error);
  };
  
  const handleReviewCode = () => {
    window.open(
      `${githubLink
        .replace("github1s.com", "github.com")
        .replace(/\/tree\/[^/]+/, "/pulls")}`,
      "_blank"
    );
  };
  
  const formatPreTags = (htmlString: string) => {
    if (!htmlString) {
      return "";
    }
    return htmlString
      .replace(
        /<pre class="(.*?)">(.*?)<\/pre>/gs,
        (_match, attributes, content) => {
          let formattedContent = content;
          
          try {
            const jsonContent = JSON.parse(content);
            formattedContent = JSON.stringify(jsonContent, null, 2);
            formattedContent = formattedContent.replace("\"", "");
          } catch (e) {
            // If it's not valid JSON, leave it as is
          }
          
          return `<pre class="whitespace-pre-wrap break-words p-4 bg-gray-100 rounded-md overflow-auto text-sm font-mono ${attributes}">${formattedContent}</pre>`;
        }
      )
      .replace(
        /<pre>(.*?)<\/pre>/gs,
        (_match, content) => {
          let formattedContent = content;
          
          try {
            const jsonContent = JSON.parse(content);
            formattedContent = JSON.stringify(jsonContent, null, 2);
          } catch (e) {
            // If it's not valid JSON, leave it as is
          }
          
          return `<pre class="whitespace-pre-wrap break-words p-4 bg-gray-100 rounded-md overflow-auto text-sm font-mono">${formattedContent}</pre>`;
        }
      );
  };
  
  useEffect(() => {
    loadShred();
    getStatus();
  }, []);
  
  useEffect(() => {
    handleShredResponse(shredResponse);
  }, [shredResponse]);
  
  // Determine if stepper should be visible
  const shouldShowStepper = (!hasError && !shredDone && currentStep !== "DONE") || hasError;
  
  // Add this helper function to determine if IDE should be shown
  const shouldShowIDE = () => {
    // Show IDE when completely done OR when code generation is complete and repo is uploaded
    return (shredDone && currentStep === "DONE") ||
      (repoUploaded && (currentStep === "COMPILING_CODE" || currentStep === "GENERATING_TESTS" || currentStep === "DONE"));
  };
  
  // Effect to handle scrolling to IDE - only when transitioning to final completion
  useEffect(() => {
    const isIDEVisible = shouldShowIDE();
    const isFullyComplete = !hasError && shredDone && currentStep === "DONE";
    
    // Only scroll if IDE is visible, fully complete, and we haven't scrolled yet
    if (isIDEVisible && isFullyComplete && !hasScrolledToIDE && ideRef.current) {
      // Use a small delay to ensure DOM is fully rendered
      setTimeout(() => {
        if (ideRef.current) {
          ideRef.current.scrollIntoView({ behavior: "smooth" });
          setHasScrolledToIDE(true);
        }
      }, 100);
    }
    
  }, [shouldShowIDE(), hasError, shredDone, currentStep, hasScrolledToIDE]);
  
  console.log("DEBUG - StepFive.tsx - RENDER - githubLink value:", githubLink);
  
  return (
    <>
      {/* Modern Header Cards */}
      <div className="grid grid-cols-12 gap-6 mx-auto w-full min-w-[200px] py-6">
        <PublishForReview shredId={shredId} onPublish={handlePublishForReview} onPublishError={handlePublishError} />
        <ShredDetails
          shredName={shredName}
          shredProgress={progress}
          shredStartDate={startDate}
          shredCoinsUsed={coinsUsed}
          shredGenerationTime={generationTime}
          isShredMode={isShredModel}
        />
      </div>
      
      <Tabs defaultValue="tab1" variant="pill">
        <TabList>
          <TabNav value="tab1">Shred Repo</TabNav>
          <TabNav value="tab2">Shred Inputs/outputs</TabNav>
          <TabNav value="tab3">Shred Description</TabNav>
          <TabNav value="tab4">Shred Plan</TabNav>
        </TabList>
        <div className="p-4">
          <TabContent value="tab1">
            <div className="space-y-6">
              {/* Error Block */}
              
              {/* Stepper Block - Show when in progress OR when done OR when error */}
              {(shouldShowStepper || (!hasError && shredDone && currentStep === "DONE")) && (
                <>
                  {/* Conditional Block - Show Push to Repository or GitHub Review Button */}
                  {!hasError && shredDone && currentStep === "DONE" && (
                    <div className="mb-8 flex justify-center">
                      {failedToPush ? (
                        // Push to Repository Block - Show when push failed
                        <div className="w-full bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                <Upload className="w-4 h-4 text-yellow-600" />
                              </div>
                              <div>
                                <h3 className="text-sm font-medium text-yellow-800">Repository Push Failed</h3>
                                <p className="text-sm text-yellow-700">The code generation completed successfully, but
                                  failed to push to the repository.</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              color="yellow-600"
                              variant="twoTone"
                              loading={pushLoading}
                              onClick={handlePushToRepo}
                              icon={<Upload />}
                            >
                              Push to Repository
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // GitHub Review Button - Show when push succeeded
                        githubLink && (
                          <button
                            onClick={handleReviewCode}
                            className="group relative inline-flex items-center gap-3 text-white font-bold px-10 py-5 rounded-2xl transition-all duration-500 shadow-2xl transform hover:scale-105 text-xl"
                            style={{
                              background: "linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 25%, #2d2d2d 50%, #5a5a5a 75%, #1a1a1a 100%)"
                            }}
                          >
                            {/* Animated background glow */}
                            <div
                              className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                              style={{
                                background: "radial-gradient(circle at center, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.15) 35%, rgba(168, 85, 247, 0.1) 70%, transparent 100%)",
                                filter: "blur(2px)"
                              }}
                            />
                            
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 rounded-2xl overflow-hidden">
                              <div
                                className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1500 ease-out"
                                style={{
                                  background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)",
                                  transform: "skewX(-25deg)"
                                }}
                              />
                            </div>
                            
                            {/* Pulsing ring effect */}
                            <div
                              className="absolute inset-0 rounded-2xl border-2 border-purple-400/20 group-hover:border-purple-400/40 transition-colors duration-300">
                              <div
                                className="absolute inset-0 rounded-2xl border border-blue-400/10 group-hover:border-blue-400/30 transition-colors duration-300 animate-pulse"></div>
                            </div>
                            
                            <Github className="w-8 h-8 z-10 relative drop-shadow-lg" />
                            <span className="z-10 relative drop-shadow-lg">Review Code on GitHub</span>
                            
                            {/* Glowing orbital dots */}
                            <div className="flex gap-2 z-10 relative">
                              <div
                                className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-lg shadow-green-400/50 animate-pulse"
                                style={{ animationDelay: "0s", animationDuration: "2s" }} />
                              <div
                                className="w-3 h-3 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full shadow-lg shadow-blue-400/50 animate-pulse"
                                style={{ animationDelay: "0.3s", animationDuration: "2s" }} />
                              <div
                                className="w-3 h-3 bg-gradient-to-r from-purple-400 to-violet-500 rounded-full shadow-lg shadow-purple-400/50 animate-pulse"
                                style={{ animationDelay: "0.6s", animationDuration: "2s" }} />
                            </div>
                            
                            {/* Subtle particles effect */}
                            <div className="absolute inset-0 pointer-events-none">
                              <div className="absolute top-2 left-4 w-1 h-1 bg-white/30 rounded-full animate-ping"
                                   style={{ animationDelay: "1s", animationDuration: "3s" }}></div>
                              <div className="absolute top-4 right-6 w-1 h-1 bg-purple-300/40 rounded-full animate-ping"
                                   style={{ animationDelay: "2s", animationDuration: "3s" }}></div>
                              <div className="absolute bottom-3 left-8 w-1 h-1 bg-blue-300/40 rounded-full animate-ping"
                                   style={{ animationDelay: "0.5s", animationDuration: "3s" }}></div>
                            </div>
                          </button>
                        )
                      )}
                    </div>
                  )}
                  
                  <GenerationStepper
                    currentStep={currentStep}
                    timeRemaining={countdown}
                    isPRScenario={isPRScenario}
                    onCancel={shouldShowStepper ? onShredStop : undefined}
                    showExtendedMessage={countdown <= 0 && inProgress}
                    failedStep={failedStep}
                    generationState={generationState}
                    hasError={hasError}
                    onResume={onShredResume}
                    resumeLoading={loading}
                  />
                
                </>
              )}
              
              {/* Action Buttons Block - Only show when completely done */}
              {!hasError && shredDone && currentStep === "DONE" && (
                <div className="pb-3 flex justify-between">
                  <Button
                    size="sm"
                    color="purple-900"
                    variant="twoTone"
                    className="px-8"
                    loading={regenerateShredLoading}
                    onClick={() => {
                      if (window.confirm("Are you sure you want to regenerate the shred?\nThis will delete the current shred and generate a new one.")) {
                        onRegenerateShred();
                      }
                    }}
                    icon={<RefreshCw />}
                  >
                    Regenerate the Shred
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="twoTone"
                    className="ml-auto px-8"
                    onClick={handleReviewCode}
                    icon={<GitPullRequest />}
                  >
                    Review Code
                  </Button>
                </div>
              )}
              
              {/* GitHub IDE Block - Show when repo is ready and code generation is complete */}
              {shouldShowIDE() && (
                <div
                  ref={ideRef}
                  className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700">Code Repository</h3>
                  </div>
                  {githubLink !== "" ? (
                    <div className="min-h-[600px]">
                      <GithubRepoBrowser key={githubKey} repoUrl={githubLink} />
                    </div>
                  ) : (
                    <div className="min-h-[600px] flex items-center justify-center bg-yellow-50 relative">
                      <div className="absolute inset-0 bg-yellow-100 opacity-90"></div>
                      <div className="relative z-10 text-center p-8 max-w-md">
                        <div className="text-yellow-600 mb-4">
                          <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-yellow-800 mb-2">GitHub Token Issue</h3>
                        <p className="text-yellow-700 mb-4">
                          Your GitHub token has expired or been revoked. Please update it in settings to view the code repository.
                        </p>
                        <a 
                          href="/settings" 
                          className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                        >
                          Go to Settings
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabContent>
          
          <TabContent value="tab2">
            <div>
              <div
                className="grid grid-cols-12 gap-3 w-full mx-auto min-w-[200px] bg-shred rounded-3xl relative p-6 mb-3"
              >
                <h1 className="text-xl text-white mb-3 col-span-12">
                  Input / Output
                </h1>
                <Apis apis={apis} setApis={(apis) => setApis(apis)} preview />
              </div>
              
              <div>
                {dependency && dependency.length > 0 && (
                  <Dependencies
                    dependency={dependency}
                    setDependency={setDependency}
                    preview
                  />
                )}
              </div>
            </div>
          </TabContent>
          
          <TabContent value="tab3">
            <div
              className=" border-gray-300 w-full whitespace-pre-wrap transition-max-height"
            >
              <CKEditorReadonly content={shredDescription} showToggleButton={false} />
            </div>
          </TabContent>
          
          <TabContent value="tab4">
            <div
              className="text-gray-800 border-gray-300 container transition-max-height"
              dangerouslySetInnerHTML={{ __html: formatPreTags(shredPlan) }}
            />
          </TabContent>
        </div>
      </Tabs>
    </>
  );
};

export default StepFive;